import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import cn from 'classnames';
import {
  CREATE_CLIENT_COMMENT_AS_ADMIN,
  GET_ALL_CLIENT_COMMENTS_BY_APPOINTMENT,
} from '../../../../graphql/clientComments';
import { useGQLLazyQuery, useGQLMutation } from '../../../../ui/utils/graphql';
import styles from './AppointmentCommentBox.module.scss';
import {
  Appointment,
  AppointmentStatus,
  ClientComment,
  CreateClientCommentAsAdminMutationVariables,
  TypeOfComment,
  TypeOfCreatedBy,
} from '../../../../gql/graphql';
import { Button } from '../../../../ui/components/Button';
// import { Icon } from '../../../../ui/components/Icon';
import { Input } from '../../../../ui/components/Input/Input';
import { Tag } from '../../../../ui/components/Tag/Tag';

interface AppointmentCommentBoxI {
  appointment: Appointment;
}

export const AppointmentCommentBox = ({
  appointment,
}: AppointmentCommentBoxI) => {
  const [comments, setComments] = useState<ClientComment[]>([]);
  const { register, handleSubmit, reset } = useForm();
  const ref = useRef<HTMLDivElement>(null);
  const { mutation } = useGQLMutation<
    ClientComment,
    CreateClientCommentAsAdminMutationVariables
  >(CREATE_CLIENT_COMMENT_AS_ADMIN, {
    onCompleted: (commentCreated: ClientComment) => {
      setComments((prevComments) => [...prevComments, commentCreated]);
      reset();
    },
  });
  const { lazy } = useGQLLazyQuery(GET_ALL_CLIENT_COMMENTS_BY_APPOINTMENT, {
    onCompleted: (commentsFetched: ClientComment[]) => {
      setComments(commentsFetched);
    },
  });

  useEffect(() => {
    lazy({
      variables: {
        appointmentId: appointment?.id,
      },
    });
  }, [appointment]);

  const onSubmit = ({ comment }: { comment: string }) => {
    if (comment.length > 0) {
      mutation({
        variables: {
          appointmentId: appointment?.id,
          clientId: appointment.Client?.id,
          comment,
        },
      });
    }
  };

  const getPlaceholderInInput = () => {
    if (
      appointment?.status === AppointmentStatus.Completed ||
      appointment?.status === AppointmentStatus.Canceled ||
      appointment?.status === AppointmentStatus.Expired
    ) {
      return 'No puedes añadir comentarios a esta cita';
    }
    return 'Añade un comentario...';
  };

  const renderAppointmentComments = () => {
    const getNameOfCreator = (comment: ClientComment) => {
      const { typeOfCreatedBy } = comment;
      const shouldDisableCreatorName = comment.type.some(
        (type) => type === TypeOfComment.Notification
      );
      const finalTypeOfCreatedBy = shouldDisableCreatorName
        ? 'Anonymous'
        : typeOfCreatedBy;
      const createdAt = new Date(Number(comment?.createdAt)).toLocaleString(
        'es-ES',
        {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }
      );
      switch (finalTypeOfCreatedBy) {
        case TypeOfCreatedBy.Client:
          return `${comment?.Client?.name} ${comment?.Client?.lastName} - ${createdAt}`;
        case TypeOfCreatedBy.Admin:
          return `${comment?.User?.name} ${comment?.User?.lastName} - ${createdAt}`;
        default:
          return `${createdAt} - `;
      }
    };

    if (!comments || comments?.length === 0) {
      return (
        <div className={styles['no-comments']}>
          No hay comentarios en esta cita.
        </div>
      );
    }

    return comments?.map((comment) => (
      <div
        key={comment.id}
        className={cn(
          styles.comment,
          comment?.typeOfCreatedBy === TypeOfCreatedBy.Admin &&
            styles['my-comment'],
          comment.type.some((type) => type === TypeOfComment.Notification) &&
            styles.notification
        )}
      >
        <div className={styles.creator}>
          <div>{getNameOfCreator(comment)}</div>
          {!comment.type.some((type) => type === TypeOfComment.Notification) &&
            comment?.type.map((type) => {
              return (
                <Tag
                  key={comment.id}
                  className={styles['comment-type']}
                  text={type}
                />
              );
            })}
        </div>
        <div className={styles.text}>{comment?.comment}</div>
      </div>
    ));
  };

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = ref.current.scrollHeight;
    }
  }, [ref, comments]);

  return (
    <div ref={ref} className={styles['accordion-body']}>
      <div className={styles.comments}>{renderAppointmentComments()}</div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={styles['create-comment']}
      >
        {/* <Button className={styles['expand-button']}>
          <Icon type="expand" alt="Expand comments" />
        </Button> */}
        <Input
          classname={styles.input}
          readOnly={
            appointment?.status === AppointmentStatus.Completed ||
            appointment?.status === AppointmentStatus.Canceled ||
            appointment?.status === AppointmentStatus.Expired
          }
          register={register('comment')}
          name="comment"
          type="text"
          placeholder={getPlaceholderInInput()}
        />
        <Button
          isDisabled={
            appointment?.status === AppointmentStatus.Completed ||
            appointment?.status === AppointmentStatus.Canceled ||
            appointment?.status === AppointmentStatus.Expired
          }
          type="submit"
          className={styles.button}
        >
          Crear
        </Button>
      </form>
    </div>
  );
};
